import { useState, useEffect, useCallback } from 'react';

/**
 * Hook to manage PWA updates with prompt or auto-update options
 */
export const useAppUpdate = () => {
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [registration, setRegistration] = useState(null);
  const [waitingWorker, setWaitingWorker] = useState(null);

  // Get user's auto-update preference from localStorage
  const getAutoUpdatePreference = () => {
    const pref = localStorage.getItem('courtiq_auto_update');
    return pref === 'true';
  };

  const setAutoUpdatePreference = (enabled) => {
    localStorage.setItem('courtiq_auto_update', enabled ? 'true' : 'false');
  };

  // Apply update - refresh the app
  const applyUpdate = useCallback(() => {
    if (waitingWorker) {
      // Tell the waiting service worker to skip waiting
      waitingWorker.postMessage({ type: 'SKIP_WAITING' });
    }
  }, [waitingWorker]);

  // Dismiss update prompt
  const dismissUpdate = useCallback(() => {
    setUpdateAvailable(false);
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) {
      console.log('[Update] Service workers not supported');
      return;
    }

    const registerSW = async () => {
      try {
        const reg = await navigator.serviceWorker.register('/sw.js', {
          scope: '/'
        });
        setRegistration(reg);
        console.log('[Update] Service Worker registered');

        // Check if there's already a waiting worker
        if (reg.waiting) {
          console.log('[Update] Found waiting worker on load');
          setWaitingWorker(reg.waiting);
          handleUpdate(reg.waiting);
        }

        // Listen for new service worker installing
        reg.addEventListener('updatefound', () => {
          console.log('[Update] New service worker found');
          const newWorker = reg.installing;
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              console.log('[Update] Worker state:', newWorker.state);
              
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                // New version installed and waiting
                console.log('[Update] New version ready');
                setWaitingWorker(newWorker);
                handleUpdate(newWorker);
              }
            });
          }
        });

        // Periodically check for updates (every 30 minutes)
        setInterval(() => {
          console.log('[Update] Checking for updates...');
          reg.update();
        }, 30 * 60 * 1000);

        // Check for update on visibility change (when app comes to foreground)
        document.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'visible') {
            console.log('[Update] App visible, checking for updates...');
            reg.update();
          }
        });

      } catch (error) {
        console.error('[Update] Service Worker registration failed:', error);
      }
    };

    const handleUpdate = (worker) => {
      const autoUpdate = getAutoUpdatePreference();
      
      if (autoUpdate) {
        // Auto-update: apply immediately
        console.log('[Update] Auto-update enabled, applying update...');
        setTimeout(() => {
          worker.postMessage({ type: 'SKIP_WAITING' });
        }, 1000);
      } else {
        // Show prompt
        console.log('[Update] Showing update prompt');
        setUpdateAvailable(true);
      }
    };

    // Listen for controller change (new SW activated)
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      console.log('[Update] Controller changed, reloading...');
      window.location.reload();
    });

    registerSW();

  }, []);

  return {
    updateAvailable,
    applyUpdate,
    dismissUpdate,
    getAutoUpdatePreference,
    setAutoUpdatePreference
  };
};

export default useAppUpdate;
